import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import {
  type SessionStoreState,
  initialState,
} from '@/hooks/use-session-store/session-store-schema';
import type { MilestoneId } from '@/lib/wizard/wizard';

interface SessionStoreActions {
  updateState: (
    values:
      | Partial<SessionStoreState>
      | ((current: SessionStoreState) => Partial<SessionStoreState>)
  ) => SessionStoreState;
  resetState: () => void;
}

type SessionStore = SessionStoreState & SessionStoreActions;

export const useSessionStore = create<SessionStore>()(
  devtools(
    persist(
      (set, get, store) => ({
        ...initialState,

        updateState: (values) => {
          if (typeof values === 'function') {
            set((current) => ({ ...current, ...values(current) }));
          } else {
            set(values);
          }

          return get();
        },

        resetState: () => set(store.getInitialState()),
      }),
      {
        name: 'session-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    {
      name: 'SessionStore',
      enabled: import.meta.env.MODE !== 'test',
    }
  )
);

export function addProductIds(productIds: string | string[]) {
  const { selectedProductIds, updateState } = useSessionStore.getState();
  const idsToAdd = (Array.isArray(productIds) ? productIds : [productIds]).filter(Boolean);
  if (idsToAdd.length === 0) return;
  const newIds = [...new Set([...selectedProductIds, ...idsToAdd])];
  updateState({ selectedProductIds: newIds });
}

export function removeProductIds(productIds: string | string[]) {
  const { selectedProductIds, updateState } = useSessionStore.getState();
  const idsToRemove = new Set(Array.isArray(productIds) ? productIds : [productIds]);
  const newIds = selectedProductIds.filter((id) => !idsToRemove.has(id));
  updateState({ selectedProductIds: newIds });
}

export function setExitMilestone(milestoneId: MilestoneId) {
  useSessionStore.getState().updateState({ exitMilestone: milestoneId });
}
